import { useState } from 'react';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { AlertCircle, Loader2, LogIn, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLogin } from '@/hooks/use-auth';
import { isAuthenticated, useAuthStore } from '@/lib/auth-store';
import { mintDevToken } from '@/lib/dev-token';

export const Route = createFileRoute('/login')({
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/' });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const login = useLogin();
  const setToken = useAuthStore((s) => s.setToken);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    login.mutate(
      { email, password },
      { onSuccess: () => void navigate({ to: '/' }) },
    );
  }

  async function onDevToken() {
    const jwt = await mintDevToken('admin');
    setToken(jwt);
    void navigate({ to: '/' });
  }

  const errorMessage = login.error instanceof Error ? login.error.message : null;

  return (
    <div className="grid min-h-screen place-items-center bg-blaze-base px-4 text-foreground">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <img src="/blaze-mark.svg" alt="BlazeIPAM" className="size-10" />
          <h1 className="text-xl font-bold tracking-tight">
            Blaze
            <span className="mx-[1px] inline-block size-2 rounded-full bg-primary align-middle shadow-[0_0_10px_var(--accent-cyan)]" />
            IPAM
          </h1>
          <p className="text-xs text-muted-foreground">
            Gestión de direccionamiento IP dual-stack para ISPs
          </p>
        </div>

        <div className="rounded-lg border border-border bg-blaze-elev1 p-5 shadow-xl">
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="email">Correo</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="noc@tu-isp.do"
                autoComplete="email"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-[11px] text-muted-foreground hover:text-primary"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            </div>

            {errorMessage && (
              <p className="flex items-center gap-1.5 text-xs text-destructive">
                <AlertCircle className="size-3.5" />
                {errorMessage}
              </p>
            )}

            <Button type="submit" className="w-full gap-2" disabled={login.isPending}>
              {login.isPending ? <Loader2 className="size-4 animate-spin" /> : <LogIn className="size-4" />}
              Entrar
            </Button>
          </form>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            ¿No tienes cuenta?{' '}
            <Link to="/register" className="font-medium text-primary hover:underline">
              Regístrate
            </Link>
          </p>

          {import.meta.env.DEV && (
            <>
              <div className="my-4 flex items-center gap-3 text-[10px] uppercase tracking-wider text-blaze-fg-muted">
                <span className="h-px flex-1 bg-border" />
                solo desarrollo
                <span className="h-px flex-1 bg-border" />
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2"
                onClick={onDevToken}
              >
                <Zap className="size-4 text-primary" />
                Entrar con token dev (admin)
              </Button>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-[10px] text-blaze-fg-muted">
          Siempre a tu servicio. © BlazeSphere Technology
        </p>
      </div>
    </div>
  );
}
